/**
 * LE RÉGLAGE DU MANUEL VIVANT — onglet « Comptes » de l'administration
 * (chantier « manuel vivant », MV-SOC-01).
 *
 * Le second étage du drapeau `MANUEL_VIVANT` : la capacité que le serveur
 * accorde à un compte, par FOYER ou par ORGANISATION. Elle se règle ici, à
 * chaud, sans build ni déploiement.
 *
 * ─── Pourquoi cet écran existe dès la phase des garde-fous ──────────────────
 *
 * La préproduction est partagée avec les testeurs TestFlight (F-18), et tout
 * est éteint par défaut — foyers de démonstration et de recette compris. Sans
 * ce réglage, la route `PUT` existerait et personne n'aurait de quoi l'appeler :
 * une capacité que seul un `curl` allume est un mécanisme sans appelant.
 *
 * ─── Ce que l'écran dit, et ce qu'il ne promet pas ──────────────────────────
 *
 * - Allumer n'affiche RIEN de plus tant que le build du client n'embarque pas
 *   le chantier : c'est l'autre étage, et l'écran le rappelle.
 * - L'effet atteint une session déjà ouverte à son prochain renouvellement de
 *   jeton (un quart d'heure au plus) ; éteindre ferme les routes tout de suite.
 * - Une organisation allumée atteint ses comptes et les élèves qu'une
 *   affectation active lui rattache, ainsi que leurs parents.
 *
 * Ce composant n'est monté que si `MANUEL_VIVANT` (build) est vrai — voir
 * `AdministrationPage`. Il ne lit PAS la capacité du compte connecté : le
 * réglage ne peut pas dépendre de ce qu'il sert à allumer.
 */
import { useCallback, useState, type FormEvent } from 'react';

import type { CibleCapacite, CompteListeItemDto, EtatCapacitesResponse } from '@silex/shared';

import { api } from '../api/client';
import { messageErreur, useAsyncRechargeable } from '../lib/useAsync';

/**
 * CE QUE L'ÉCRAN DIT DE LA PORTÉE — refonte de la console, L1.
 *
 * Il disait toujours « Éteint par défaut pour tous les comptes », alors que la
 * production allume le manuel pour TOUS (`MANUEL_VIVANT_POUR_TOUS`) : un
 * réglage de la plateforme, que la réponse de `GET /v1/admin/capacites` porte
 * désormais (`pourTous`). Absent (serveur d'avant) ou faux : l'ancien texte,
 * qui est alors vrai.
 */
export function direPorteeManuel(etat: Pick<EtatCapacitesResponse, 'pourTous'> | null): string {
  return etat?.pourTous === true
    ? 'Allumé pour tous les comptes (réglage de la plateforme). Les réglages par foyer et par organisation ci-dessous ne changent rien tant que ce réglage reste allumé.'
    : 'Éteint par défaut pour tous les comptes. Un compte le voit si son foyer ou une organisation qui le suit est allumé.';
}

export function ReglageManuelVivant() {
  const charger = useCallback(() => api.etatCapacites(), []);
  const { resource: etat, recharger } = useAsyncRechargeable<EtatCapacitesResponse>(charger);

  /* `enCours` porte l'identifiant de la CIBLE, pas un booléen : deux gestes sur
     deux lignes ne se désactivent pas l'un l'autre. */
  const [enCours, setEnCours] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [succes, setSucces] = useState<string | null>(null);

  const [saisie, setSaisie] = useState('');
  const [trouves, setTrouves] = useState<readonly CompteListeItemDto[] | null>(null);
  const [recherche, setRecherche] = useState(false);

  async function regler(cible: CibleCapacite, id: string, nom: string, manuelVivant: boolean) {
    setEnCours(id);
    setErreur(null);
    setSucces(null);
    try {
      const r = await api.reglerManuelVivant(cible, id, manuelVivant);
      const quoi = cible === 'foyer' ? `le foyer de ${nom}` : `l'organisation ${nom}`;
      setSucces(
        r.capacites.manuelVivant
          ? `Le manuel vivant est allumé pour ${quoi}. Il apparaîtra au prochain renouvellement de session, dans un client dont le build l'embarque.`
          : `Le manuel vivant est éteint pour ${quoi}. Ses routes sont fermées dès maintenant.`,
      );
      recharger();
    } catch (err: unknown) {
      setErreur(messageErreur(err));
    } finally {
      setEnCours(null);
    }
  }

  async function chercher(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const q = saisie.trim();
    if (!q) return;
    setRecherche(true);
    setErreur(null);
    try {
      /* Une recherche nommée : les comptes de test y restent trouvables (D-7). */
      const r = await api.listComptes({ q, inclureTests: true });
      setTrouves(r.comptes);
    } catch (err: unknown) {
      setErreur(messageErreur(err));
    } finally {
      setRecherche(false);
    }
  }

  return (
    <section className="card" aria-labelledby="reglage-manuel-vivant">
      <h3 id="reglage-manuel-vivant">Manuel vivant</h3>
      <p className="suivi-note" data-testid="reglage-manuel-portee">
        {direPorteeManuel(etat.status === 'ready' ? etat.data : null)}
      </p>

      {etat.status === 'loading' ? <p className="suivi-note">Chargement…</p> : null}
      {etat.status === 'error' ? (
        <div className="callout callout--rare" role="alert">
          <strong>Chargement impossible.</strong> {etat.message}
        </div>
      ) : null}

      {etat.status === 'ready' ? (
        <>
          <h4>Organisations</h4>
          {etat.data.organisations.length === 0 ? (
            <p className="suivi-note">Aucune organisation n'existe.</p>
          ) : (
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th scope="col">Organisation</th>
                    <th scope="col">Manuel vivant</th>
                    <th scope="col">
                      <span className="sr-only">Action</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {etat.data.organisations.map((o) => (
                    <tr key={o.id}>
                      <td>{o.nom}</td>
                      <td>
                        <span className="tag">{o.manuelVivant ? 'Allumé' : 'Éteint'}</span>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          disabled={enCours === o.id}
                          onClick={() => void regler('organisation', o.id, o.nom, !o.manuelVivant)}
                        >
                          {o.manuelVivant ? 'Éteindre' : 'Allumer'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <h4>Foyers allumés</h4>
          {etat.data.foyers.length === 0 ? (
            <p className="suivi-note">Aucun foyer n'est allumé.</p>
          ) : (
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th scope="col">Titulaire du foyer</th>
                    <th scope="col">
                      <span className="sr-only">Action</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {etat.data.foyers.map((f) => (
                    <tr key={f.id}>
                      <td>{f.courriel}</td>
                      <td>
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          disabled={enCours === f.id}
                          onClick={() => void regler('foyer', f.id, f.courriel, false)}
                        >
                          Éteindre
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <h4>Allumer un foyer</h4>
          <form className="row" onSubmit={(e) => void chercher(e)}>
            <span className="field field--inline" style={{ width: 260 }}>
              <input
                className="control"
                type="search"
                aria-label="Chercher le titulaire d'un foyer"
                placeholder="Nom ou courriel du titulaire…"
                value={saisie}
                onChange={(e) => setSaisie(e.target.value)}
                style={{ minHeight: 40 }}
              />
            </span>
            <button type="submit" className="btn btn--ghost btn--sm" disabled={recherche}>
              Chercher
            </button>
          </form>
          {trouves !== null && trouves.length === 0 ? (
            <p className="suivi-note">Aucun compte ne correspond.</p>
          ) : null}
          {trouves !== null && trouves.length > 0 ? (
            <div className="table-wrap">
              <table className="data-table">
                <tbody>
                  {trouves.map((c) => {
                    const allume = etat.data.foyers.some((f) => f.id === c.id);
                    return (
                      <tr key={c.id}>
                        <td>
                          <div style={{ fontWeight: 600 }}>
                            {c.prenom} {c.nom}
                          </div>
                          <div className="cell-sub">{c.email}</div>
                        </td>
                        <td>
                          <span className="tag">{allume ? 'Allumé' : 'Éteint'}</span>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="btn btn--ghost btn--sm"
                            disabled={enCours === c.id}
                            onClick={() => void regler('foyer', c.id, c.email, !allume)}
                          >
                            {allume ? 'Éteindre' : 'Allumer'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : null}
        </>
      ) : null}

      {erreur ? (
        <div className="callout callout--rare" role="alert">
          {erreur}
        </div>
      ) : null}
      {succes ? (
        <p className="suivi-note" role="status">
          {succes}
        </p>
      ) : null}
    </section>
  );
}
